import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH, API_UPDATE } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { SELECTED_SITE_STORAGE_KEY } from '../AuthReducer/authSlice';

// ========================================
// TYPES
// ========================================
export interface CurrentUser {
    userId: string;        // Keep for internal use
    workdayId: string;     // Primary identifier from backend
    firstName: string;
    lastName: string;
    email: string;
    phoneNbr: string;
    siteCd: string;
    skills: string[];
    team?: string;         // Added team field
    role: 'User' | 'Supervisor' | 'Administrator' | 'SuperUser';
    status: string;
    isSuperUser: boolean;
}

export interface UpdateUserProfilePayload {
    workdayId: string;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNbr?: string;
    skills?: string[];
    team?: string;         // Added team field
    eventAction?: string[]; // send array of event actions to match backend List<string>
}

interface CurrentUserState {
    user: CurrentUser | null;
    loading: boolean;
    error: string | null;
    updating: boolean;
    updateError: string | null;
}

const initialState: CurrentUserState = {
    user: null,
    loading: false,
    error: null,
    updating: false,
    updateError: null,
};

// ========================================
// ASYNC THUNKS
// ========================================

/**
 * Fetch the current authenticated user's profile
 * Uses: GET /api/user/me
 */
export const fetchCurrentUser = createAsyncThunk(
    'currentUser/fetchCurrentUser',
    async (_, { rejectWithValue }) => {
        try {

            const response = await API_FETCH(ENDPOINTS.GET_CURRENT_USER_PROFILE);

            const isSuperUser = response.role === 'SuperUser';
            const backendSiteCd = response.siteCd || '';

            // For super users, prefer any previously selected site persisted in
            // localStorage so a page refresh doesn't reset them back to their
            // home site (e.g. "ALL"). Regular users always use the backend value.
            const persistedSiteCd = isSuperUser
                ? localStorage.getItem(SELECTED_SITE_STORAGE_KEY)
                : null;

            //Map backend response to frontend interface
            const mappedUser: CurrentUser = {
                userId: response.workdayId || '',      
                workdayId: response.workdayId || '',
                firstName: response.firstName || '',
                lastName: response.lastName || '',
                email: response.email || '',
                phoneNbr: response.phoneNbr || '',
                siteCd: persistedSiteCd || backendSiteCd,
                skills: Array.isArray(response.skills) ? response.skills : [],
                team: response.team || response.teamCd || '',    // Map team field
                role: response.role || 'User',
                status: response.status || '',
                isSuperUser,
            };

            console.log('✅ Mapped user:', mappedUser);
            return mappedUser;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch current user');
        }
    }
);

/**
 * Update the current user's profile
 * Uses: PUT /api/User/profile/{workdayId}
 */
export const updateCurrentUserProfile = createAsyncThunk(
    'currentUser/updateProfile',
    async (payload: UpdateUserProfilePayload, { rejectWithValue }) => {
        try {
            const { workdayId, ...updateData } = payload;
            
            const url = `${ENDPOINTS.UPDATE_USER_PROFILE}/${workdayId}`;
            const response = await API_UPDATE(url, updateData);

            const isSuperUser = response.role === 'SuperUser';
            const backendSiteCd = response.siteCd || '';
            const persistedSiteCd = isSuperUser
                ? localStorage.getItem(SELECTED_SITE_STORAGE_KEY)
                : null;

            //Map response back
            const mappedUser: CurrentUser = {
                userId: response.workdayId || workdayId,
                workdayId: response.workdayId || workdayId,
                firstName: response.firstName || '',
                lastName: response.lastName || '',
                email: response.email || '',
                phoneNbr: response.phoneNbr || '',
                siteCd: persistedSiteCd || backendSiteCd,
                skills: Array.isArray(response.skills) ? response.skills : [],
                team: response.team || response.teamCd || '',    // Map team field
                role: response.role || 'User',
                status: response.status || '',
                isSuperUser,
            };

            return mappedUser;
        } catch (error: any) {
            return rejectWithValue(error?.data?.message || error?.message || 'Failed to update profile');
        }
    }
);

// ========================================
// SLICE
// ========================================
const currentUserSlice = createSlice({
    name: 'currentUser',
    initialState,
    reducers: {
        clearCurrentUser: (state) => {
            state.user = null;
            state.loading = false;
            state.error = null;
            state.updating = false;
            state.updateError = null;
        },
        clearError: (state) => {
            state.error = null;
            state.updateError = null;
        },
        setSiteCd: (state, action) => {
            if (state.user) {
                state.user.siteCd = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch current user
            .addCase(fetchCurrentUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
            })
            .addCase(fetchCurrentUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Update current user profile
            .addCase(updateCurrentUserProfile.pending, (state) => {
                state.updating = true;
                state.updateError = null;
            })
            .addCase(updateCurrentUserProfile.fulfilled, (state, action) => {
                state.updating = false;
                state.user = action.payload;
            })
            .addCase(updateCurrentUserProfile.rejected, (state, action) => {
                state.updating = false;
                state.updateError = action.payload as string;
            });
    },
});

export const { clearCurrentUser, clearError, setSiteCd } = currentUserSlice.actions;
export default currentUserSlice.reducer;